import { AxiosError, AxiosResponse, isCancel } from 'axios';
import AxiosInstance from '../../helpers/axios-instance/axios-instance';
import { appConfig } from '../../helpers/app-config/app-config';
import type { Project } from '../projects-client/projects-client.type';
import type { Epic } from '../tasks-client/task-client.type';
import type { Portfolio, PortfolioPayload, ProcessAnalysisInitiativeChartData } from './portfolio-client.type';

const axiosInstance = AxiosInstance();
const baseURL = appConfig.VITE_APP_API_URL + '/api/portfolios/';

/**
 * Method to create a new portoflio
 *
 * @param {PortfolioPayload} payload a PortfolioPayload object
 * @returns a Portfolio object
 */
const createPortfolio = async (payload: PortfolioPayload): Promise<Portfolio> => {
  try {
    const apiURL = baseURL;
    const response: AxiosResponse = await axiosInstance.post(apiURL, payload);
    return response.data;
  } catch (error: unknown) {
    if (error instanceof AxiosError) {
      return Promise.reject(error.response);
    } else {
      return Promise.reject(new Error('An unknown error occurred while creating portfolio.'));
    }
  }
};

/**
 * Method to fetch all portfolios
 *
 * @returns an array of Portfolio objects
 */
const fetchPortfolios = async (): Promise<Portfolio[]> => {
  try {
    const response: AxiosResponse = await axiosInstance.get(baseURL);
    return response.data;
  } catch (error: unknown) {
    if (error instanceof AxiosError) {
      return Promise.reject(error.response);
    } else {
      return Promise.reject(new Error('An unknown error occurred while fetching portfolios.'));
    }
  }
};

/**
 * Method to update a portfolio
 *
 * @param payload a PortfolioPayload object
 * @returns a Portfolio object
 */
const updatePortfolio = async (payload: PortfolioPayload & { id: string }): Promise<Portfolio> => {
  try {
    const apiURL = `${baseURL}${payload.id}/?user-acknowledged=true`;
    const response: AxiosResponse = await axiosInstance.put(apiURL, payload);
    return response.data;
  } catch (error: unknown) {
    if (error instanceof AxiosError) {
      return Promise.reject(error.response);
    } else {
      return Promise.reject(new Error('An unknown error occurred while updating portfolio.'));
    }
  }
};

/**
 * Method to delete a portfolio
 *
 * @param {Portfolio} portfolio a Portfolio object
 * @returns a Portfolio object
 */
const deletePortfolio = async (portfolio: Portfolio): Promise<void> => {
  try {
    const apiURL = `${baseURL}${portfolio.id}/?user-acknowledged=true`;
    const response: AxiosResponse = await axiosInstance.delete(apiURL);
    return response.data;
  } catch (error: unknown) {
    if (error instanceof AxiosError) {
      return Promise.reject(error.response);
    } else {
      return Promise.reject(new Error('An unknown error occurred while deleting portfolio.'));
    }
  }
};

/**
 * Method to fetch the initiative completions for a given portfolio
 *
 * @param {string} portfolioId portfolio id
 * @param {string} startDate start date to filter
 * @param {string} endDate end date to filter
 * @returns a object containing initiative completions
 */
const getInitiativeCompletion = async (
  portfolioId: string,
  startDate: string | null,
  endDate: string | null,
  signal?: AbortSignal,
): Promise<ProcessAnalysisInitiativeChartData> => {
  try {
    let url = baseURL + `${portfolioId}/initiatives/completion/`;
    if (startDate) {
      url += `?start_date=${startDate}`;
    }
    if (endDate) {
      url += `&end_date=${endDate}`;
    }
    const response: AxiosResponse = await axiosInstance.get(url, { signal });
    return response.data;
  } catch (error: unknown) {
    if (isCancel(error)) {
      return Promise.reject(error);
    }
    if (error instanceof AxiosError) {
      return Promise.reject(error.response);
    } else {
      return Promise.reject(new Error('An unknown error occurred while fetching initiative completions.'));
    }
  }
};

/**
 * Fetches epics for a given portfolio id
 *
 * @param {string} portfolioId portfolio id
 * @returns a list of epics
 */
const getEpicsByPortfolioId = (portfolioId: string): Promise<Epic[]> => {
  const url = baseURL + `${portfolioId}/epics/`;
  return axiosInstance
    .get(url)
    .then((response) => response.data)
    .catch((error) => Promise.reject(error?.response?.data || 'Error while fetching epics for portfolio.'));
};

/**
 * Fetches projects for a given portfolio id
 *
 * @param {string} portfolioId portfolio id
 * @param projectFields projectFields to fetch
 * @returns a list of projects
 */
async function getProjectsByPortfolioId(portfolioId: string, projectFields?: Array<keyof Project>): Promise<Project[]> {
  try {
    let url = baseURL + `${portfolioId}/projects/`;
    if (projectFields && projectFields.length) {
      const params = new URLSearchParams();
      params.set('fields', projectFields.join(','));
      url += `?${params.toString()}`;
    }
    const response: AxiosResponse = await axiosInstance.get(url);
    return response.data;
  } catch (error: unknown) {
    if (error instanceof AxiosError) {
      return Promise.reject(error.response?.data || 'Error while fetching projects for portfolio.');
    }
    return Promise.reject('Error while fetching projects for portfolio.');
  }
}

/**
 * Creates a Google Slides presentation for the given initiatives
 *
 * @param {string} portfolioId - portfolio id
 * @param {string} name - name of the presentation
 * @param {string[]} initiativeIds - array of initiative ids to include
 * @returns Promise that resolves to the URL of the created presentation
 */
const createInitiativePresentation = async (
  portfolioId: string,
  name: string,
  initiativeIds: string[],
): Promise<string> => {
  try {
    const idsParam = initiativeIds.join(',');
    const url = `${baseURL}${portfolioId}/initiatives/create-presentation/?name=${encodeURIComponent(name)}&initiative_ids=${idsParam}`;
    const response: AxiosResponse = await axiosInstance.get(url);
    return response.data.url;
  } catch (error: unknown) {
    if (error instanceof AxiosError) {
      return Promise.reject(error.response?.data?.error || 'Failed to create presentation');
    } else {
      return Promise.reject(new Error('An unknown error occurred while creating presentation.'));
    }
  }
};

/**
 * Exports goals to CSV file and triggers download
 *
 * @param {string} organizationId - organization id (required)
 * @param {string} portfolioId - portfolio id (optional)
 * @param {string[]} goalIds - optional array of goal ids to filter
 * @returns Promise that resolves when download is triggered
 */
const exportGoalsCSV = async (organizationId: string, portfolioId?: string, goalIds?: string[]): Promise<void> => {
  try {
    const strategyBaseURL = appConfig.VITE_APP_API_URL + '/api/strategy/';
    const params = new URLSearchParams();
    params.append('organization_id', organizationId);
    if (portfolioId) {
      params.append('portfolio_id', portfolioId);
    }
    if (goalIds && goalIds.length > 0) {
      params.append('goal_ids', goalIds.join(','));
    }
    const url = `${strategyBaseURL}goals/export-csv/?${params.toString()}`;
    const response: AxiosResponse = await axiosInstance.get(url, {
      responseType: 'blob',
    });
    const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
    const downloadUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.setAttribute('download', 'goals.csv');
    link.click();
    URL.revokeObjectURL(downloadUrl);
  } catch (error: unknown) {
    if (error instanceof AxiosError) {
      return Promise.reject(error.response?.data?.error || 'Failed to export goals CSV');
    } else {
      return Promise.reject(new Error('An unknown error occurred while exporting goals CSV.'));
    }
  }
};

/**
 * Creates a Google Slides presentation for the given goals
 *
 * @param {string} organizationId - organization id (required)
 * @param {string} name - name of the presentation
 * @param {string} portfolioId - portfolio id (optional)
 * @param {string[]} goalIds - optional array of goal ids to include
 * @returns Promise that resolves to the URL of the created presentation
 */
const createGoalPresentation = async (
  organizationId: string,
  name: string,
  portfolioId?: string,
  goalIds?: string[],
): Promise<string> => {
  try {
    const strategyBaseURL = appConfig.VITE_APP_API_URL + '/api/strategy/';
    const params = new URLSearchParams();
    params.append('organization_id', organizationId);
    params.append('name', name);
    if (portfolioId) {
      params.append('portfolio_id', portfolioId);
    }
    if (goalIds && goalIds.length > 0) {
      params.append('goal_ids', goalIds.join(','));
    }
    const url = `${strategyBaseURL}goals/create-presentation/?${params.toString()}`;
    const response: AxiosResponse = await axiosInstance.get(url);
    return response.data.url;
  } catch (error: unknown) {
    if (error instanceof AxiosError) {
      return Promise.reject(error.response?.data?.error || 'Failed to create goal presentation');
    } else {
      return Promise.reject(new Error('An unknown error occurred while creating goal presentation.'));
    }
  }
};

/**
 * Exports outcomes to CSV file and triggers download
 *
 * @param {string} organizationId - organization id (required)
 * @param {string} portfolioId - portfolio id (optional)
 * @param {string[]} outcomeIds - optional array of outcome ids to filter
 * @returns Promise that resolves when download is triggered
 */
const exportOutcomesCSV = async (
  organizationId: string,
  portfolioId?: string,
  outcomeIds?: string[],
): Promise<void> => {
  try {
    const strategyBaseURL = appConfig.VITE_APP_API_URL + '/api/strategy/';
    const params = new URLSearchParams();
    params.append('organization_id', organizationId);
    if (portfolioId) {
      params.append('portfolio_id', portfolioId);
    }
    if (outcomeIds && outcomeIds.length > 0) {
      params.append('outcome_ids', outcomeIds.join(','));
    }
    const url = `${strategyBaseURL}outcomes/export-csv/?${params.toString()}`;
    const response: AxiosResponse = await axiosInstance.get(url, {
      responseType: 'blob',
    });
    const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
    const downloadUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.setAttribute('download', 'outcomes.csv');
    link.click();
    URL.revokeObjectURL(downloadUrl);
  } catch (error: unknown) {
    if (error instanceof AxiosError) {
      return Promise.reject(error.response?.data?.error || 'Failed to export outcomes CSV');
    } else {
      return Promise.reject(new Error('An unknown error occurred while exporting outcomes CSV.'));
    }
  }
};

/**
 * Creates a Google Slides presentation for the given outcomes
 *
 * @param {string} organizationId - organization id (required)
 * @param {string} name - name of the presentation
 * @param {string} portfolioId - portfolio id (optional)
 * @param {string[]} outcomeIds - optional array of outcome ids to include
 * @returns Promise that resolves to the URL of the created presentation
 */
const createOutcomePresentation = async (
  organizationId: string,
  name: string,
  portfolioId?: string,
  outcomeIds?: string[],
): Promise<string> => {
  try {
    const strategyBaseURL = appConfig.VITE_APP_API_URL + '/api/strategy/';
    const params = new URLSearchParams();
    params.append('organization_id', organizationId);
    params.append('name', name);
    if (portfolioId) {
      params.append('portfolio_id', portfolioId);
    }
    if (outcomeIds && outcomeIds.length > 0) {
      params.append('outcome_ids', outcomeIds.join(','));
    }
    const url = `${strategyBaseURL}outcomes/create-presentation/?${params.toString()}`;
    const response: AxiosResponse = await axiosInstance.get(url);
    return response.data.url;
  } catch (error: unknown) {
    if (error instanceof AxiosError) {
      return Promise.reject(error.response?.data?.error || 'Failed to create outcome presentation');
    } else {
      return Promise.reject(new Error('An unknown error occurred while creating outcome presentation.'));
    }
  }
};

export {
  createGoalPresentation,
  createInitiativePresentation,
  createOutcomePresentation,
  createPortfolio,
  deletePortfolio,
  exportGoalsCSV,
  exportOutcomesCSV,
  fetchPortfolios,
  getEpicsByPortfolioId,
  getInitiativeCompletion,
  getProjectsByPortfolioId,
  updatePortfolio,
};
